import { useEffect, useId, useRef, useState } from 'react'
import type { MediaAssetSummary } from '@cms/contracts'
import { sanitizeHtml } from '../domain/html.js'
import { liveUrl } from '../domain/live-url.js'
import { altFor } from '../domain/media.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { useI18n } from '../i18n/context.js'
import { cx, Icon, IconButton } from '../ui/primitives.js'
import type { IconName } from '../ui/icons.js'
import { MediaPickerDialog } from './MediaPickerDialog.js'

type Command = { key: string; icon?: IconName; text?: string; label: string; run: () => void }

/**
 * Editor visuale del testo formattato: grassetto, titoli, elenchi, collegamenti
 * e immagini dalla libreria. Nessun tag a vista: chi scrive vede il testo come
 * sarà. Il contenuto che arriva dal vecchio sito viene ripulito da script e
 * gestori di eventi prima di essere mostrato (`sanitizeHtml`); `onChange` parte
 * solo quando la persona scrive, quindi aprire e chiudere non cambia il testo.
 */
export function RichTextEditor({ label, value, onChange, disabled, required, supporting }: {
  label: string
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  required?: boolean
  supporting?: string
}) {
  const { t, locale } = useI18n()
  const { site } = useSiteWorkspace()
  const id = useId()
  const area = useRef<HTMLDivElement | null>(null)
  const lastEmitted = useRef<string | null>(null)
  const savedRange = useRef<Range | null>(null)
  const [stripped, setStripped] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [picking, setPicking] = useState(false)

  // Allinea l'area al valore, ma solo quando il valore è cambiato da fuori: riscriverla a ogni battuta sposterebbe il cursore.
  useEffect(() => {
    const element = area.current
    if (!element || value === lastEmitted.current) return
    const clean = sanitizeHtml(value)
    element.innerHTML = clean.html
    showImages(element)
    setStripped(clean.stripped)
    lastEmitted.current = value
  }, [value])

  // Nell'editor un'immagine «/media/x.webp» non si trova (l'indirizzo è del sito pubblicato): la si mostra dal sito
  // e si ricorda l'indirizzo vero in data-path, che `stored` rimette prima di salvare.
  const showImages = (element: HTMLElement) => {
    element.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src') ?? ''
      const shown = src.startsWith('/') && !src.startsWith('//') ? liveUrl(site?.primaryDomain, src) : null
      if (shown) { img.dataset.path = src; img.setAttribute('src', shown) }
    })
  }
  const stored = (element: HTMLElement): string => {
    const copy = element.cloneNode(true) as HTMLElement
    copy.querySelectorAll('img[data-path]').forEach((img) => { img.setAttribute('src', (img as HTMLElement).dataset.path ?? ''); img.removeAttribute('data-path') })
    return copy.innerHTML
  }

  const emit = () => {
    const element = area.current
    if (!element) return
    const html = element.innerHTML === '<br>' ? '' : stored(element)
    lastEmitted.current = html
    onChange(html)
  }

  const exec = (command: string, argument?: string) => {
    area.current?.focus()
    document.execCommand(command, false, argument)
    emit()
  }

  const rememberSelection = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && area.current?.contains(selection.anchorNode)) savedRange.current = selection.getRangeAt(0).cloneRange()
  }
  const restoreSelection = () => {
    area.current?.focus()
    const selection = window.getSelection()
    if (selection && savedRange.current) { selection.removeAllRanges(); selection.addRange(savedRange.current) }
  }

  const applyLink = () => {
    restoreSelection()
    const address = linkUrl.trim()
    if (address) exec('createLink', address)
    setLinkOpen(false)
    setLinkUrl('')
  }

  const insertImage = (asset: MediaAssetSummary) => {
    setPicking(false)
    restoreSelection()
    const alt = altFor(asset.alt, locale).replace(/"/g, '&quot;')
    exec('insertHTML', `<img src="${asset.publicPath}" alt="${alt}">`)
    if (area.current) showImages(area.current)
  }

  const commands: Command[] = [
    { key: 'bold', icon: 'format_bold', label: t('editor.html.bold'), run: () => exec('bold') },
    { key: 'italic', icon: 'format_italic', label: t('editor.html.italic'), run: () => exec('italic') },
    { key: 'h2', text: 'H2', label: t('editor.html.h2'), run: () => exec('formatBlock', '<h2>') },
    { key: 'h3', text: 'H3', label: t('editor.html.h3'), run: () => exec('formatBlock', '<h3>') },
    { key: 'p', text: '¶', label: t('editor.html.paragraph'), run: () => exec('formatBlock', '<p>') },
    { key: 'ul', icon: 'format_list_bulleted', label: t('editor.html.bullets'), run: () => exec('insertUnorderedList') },
    { key: 'ol', icon: 'format_list_numbered', label: t('editor.html.numbers'), run: () => exec('insertOrderedList') },
    { key: 'quote', icon: 'format_quote', label: t('editor.html.quote'), run: () => exec('formatBlock', '<blockquote>') },
    { key: 'link', icon: 'link', label: t('editor.html.link'), run: () => { rememberSelection(); setLinkOpen(true) } },
    { key: 'unlink', icon: 'link_off', label: t('editor.html.unlink'), run: () => exec('unlink') },
    { key: 'image', icon: 'image', label: t('editor.html.image'), run: () => { rememberSelection(); setPicking(true) } },
  ]

  return <div className={cx('field', 'rich-field', disabled && 'is-disabled')}>
    <div className="field-label-row">
      <label className="field-label" id={`${id}-label`}>{label}{required && <span className="field-required" aria-hidden="true"> *</span>}</label>
    </div>
    <div className="rich-box">
      {!disabled && <div className="rich-toolbar" role="toolbar" aria-label={t('editor.html.toolbar')}>
        {commands.map((command) => command.icon
          ? <span key={command.key} onMouseDown={(event) => event.preventDefault()}><IconButton icon={command.icon} label={command.label} onClick={command.run}/></span>
          : <button key={command.key} type="button" className="rich-text-button" title={command.label} aria-label={command.label}
              onMouseDown={(event) => event.preventDefault()} onClick={command.run}>{command.text}</button>)}
      </div>}
      {linkOpen && <div className="rich-link-row">
        <Icon name="link"/>
        <input className="field-input" autoFocus value={linkUrl} placeholder={t('editor.urlPlaceholder')} aria-label={t('editor.html.linkPrompt')}
          onChange={(event) => setLinkUrl(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); applyLink() } if (event.key === 'Escape') setLinkOpen(false) }}/>
        <button type="button" className="rich-text-button wide" onClick={applyLink}>{t('editor.html.linkApply')}</button>
        <IconButton icon="close" label={t('common.cancel')} onClick={() => setLinkOpen(false)}/>
      </div>}
      <div
        ref={area}
        className="rich-area"
        role="textbox"
        aria-multiline="true"
        aria-labelledby={`${id}-label`}
        data-placeholder={t('editor.html.placeholder')}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        onInput={emit}
        onBlur={rememberSelection}
      />
    </div>
    {stripped && <p className="field-support">{t('editor.htmlStripped')}</p>}
    {supporting && <p className="field-support">{supporting}</p>}
    {picking && <MediaPickerDialog title={t('media.picker.titleFor', { field: label })} onPick={insertImage} onClose={() => setPicking(false)}/>}
  </div>
}
